/**
 * Obligations Summary Component (REAL COMPLIANCE ENGINE)
 * 
 * Step 4 of wizard - Shows which tax obligations apply to the company
 * Uses REAL Algerian compliance rules from 2024-2025 Finance Laws
 */

import { CheckCircle, XCircle, AlertCircle, ArrowLeft, Save, Calendar, FileText, TrendingUp, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { CompanyLegalProfile } from '@/types';
import { analyzeCompliance, estimateAnnualComplianceCost } from '@/lib/compliance/engine';

interface ObligationsSummaryProps {
  profile: CompanyLegalProfile;
  onConfirm: () => void;
  onBack: () => void;
}

export function ObligationsSummary({ profile, onConfirm, onBack }: ObligationsSummaryProps) {
  // Analyze compliance using REAL Algerian rules
  const analysis = analyzeCompliance(profile);
  const costEstimate = estimateAnnualComplianceCost(profile);

  const formatDeadline = (obligation: any) => {
    if (obligation.frequency === 'monthly') {
      return `Le ${obligation.deadline_day} de chaque mois`;
    }
    if (obligation.frequency === 'quarterly') {
      return `Trimestriel - Le ${obligation.deadline_day}`;
    }
    if (obligation.frequency === 'annual') {
      const months = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ];
      return `${obligation.deadline_day} ${months[(obligation.deadline_month || 1) - 1]}`;
    }
    if (obligation.frequency === 'one_time') {
      return 'Une seule fois (lors de la création)';
    }
    return 'Date variable';
  };

  const getFrequencyBadgeColor = (frequency: string) => {
    switch (frequency) {
      case 'monthly': return 'default';
      case 'quarterly': return 'secondary';
      case 'annual': return 'outline';
      case 'one_time': return 'destructive';
      default: return 'default';
    }
  };

  const getAuthorityColor = (authority: string) => {
    switch (authority) {
      case 'DGI': return 'text-blue-600';
      case 'CNAS': return 'text-green-600';
      case 'CNRC': return 'text-purple-600';
      case 'MF': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Obligations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analysis.total_obligations}</div>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-600">Applicables</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {analysis.applicable_obligations.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Non applicables</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-500">
              {analysis.not_applicable_obligations.length}
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600">Déclarations/an</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {costEstimate.total_annual}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {costEstimate.monthly_obligations}M + {costEstimate.quarterly_obligations}T + {costEstimate.annual_obligations}A
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Frequency Breakdown */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Répartition par fréquence
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{analysis.monthly.length}</div>
              <p className="text-sm text-gray-600">Mensuelles</p>
              <p className="text-xs text-gray-500">{analysis.monthly.length * 12}/an</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{analysis.quarterly.length}</div>
              <p className="text-sm text-gray-600">Trimestrielles</p>
              <p className="text-xs text-gray-500">{analysis.quarterly.length * 4}/an</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{analysis.annual.length}</div>
              <p className="text-sm text-gray-600">Annuelles</p>
              <p className="text-xs text-gray-500">{analysis.annual.length}/an</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">{analysis.one_time.length}</div>
              <p className="text-sm text-gray-600">Ponctuelles</p>
              <p className="text-xs text-gray-500">À la création</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applicable Obligations */}
      <div>
        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
          <CheckCircle className="h-5 w-5 text-green-600" />
          Obligations applicables à votre entreprise ({analysis.applicable_obligations.length})
        </h3>
        <div className="space-y-3">
          {analysis.applicable_obligations.map((obligation) => (
            <Card key={obligation.id} className="border-green-200 bg-green-50 dark:bg-green-950">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-base">{obligation.name_fr}</CardTitle>
                      <Badge variant="outline" className="text-xs">
                        {obligation.code}
                      </Badge>
                    </div>
                    {obligation.name_ar && (
                      <CardDescription className="text-right" dir="rtl">
                        {obligation.name_ar}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={getFrequencyBadgeColor(obligation.frequency)}>
                      {obligation.frequency === 'monthly' && 'Mensuel'}
                      {obligation.frequency === 'quarterly' && 'Trimestriel'}
                      {obligation.frequency === 'annual' && 'Annuel'}
                      {obligation.frequency === 'one_time' && 'Ponctuel'}
                    </Badge>
                    <Badge variant="secondary" className={getAuthorityColor(obligation.authority)}>
                      {obligation.authority}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {obligation.description_fr}
                </p>
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDeadline(obligation)}</span>
                  </div>
                  {obligation.has_penalty && (
                    <div className="flex items-center gap-1 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-xs">{obligation.penalty_description_fr}</span>
                    </div>
                  )}
                  {obligation.submission_method === 'online' && (
                    <div className="flex items-center gap-1 text-blue-600">
                      <span className="text-xs">💻 En ligne</span>
                    </div>
                  )}
                  {obligation.next_deadline && obligation.days_until_deadline !== undefined && (
                    <div className="flex items-center gap-1 text-purple-600">
                      <span className="text-xs font-semibold">
                        ⏰ Dans {obligation.days_until_deadline} jours
                      </span>
                    </div>
                  )}
                </div>
                {obligation.notes_fr && (
                  <p className="text-xs text-gray-500 italic mt-2">
                    💡 {obligation.notes_fr}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Not Applicable Obligations */}
      {analysis.not_applicable_obligations.length > 0 && (
        <div>
          <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-600">
            <XCircle className="h-5 w-5" />
            Obligations non applicables ({analysis.not_applicable_obligations.length})
          </h3>
          <div className="space-y-2">
            {analysis.not_applicable_obligations.map((obligation) => (
              <div
                key={obligation.id}
                className="flex items-center justify-between rounded-lg border bg-gray-50 p-3 dark:bg-gray-900"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    {obligation.name_fr}
                    <Badge variant="outline" className="text-xs">{obligation.code}</Badge>
                  </p>
                  {obligation.reason_not_applicable && (
                    <p className="text-xs text-gray-500 mt-1">
                      {obligation.reason_not_applicable}
                    </p>
                  )}
                </div>
                <XCircle className="h-5 w-5 text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Penalty Risk Warning */}
      {analysis.estimated_penalty_risk > 0 && (
        <div className="rounded-lg bg-red-50 p-4 dark:bg-red-950 border border-red-200 dark:border-red-800">
          <div className="flex gap-3">
            <DollarSign className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
            <div>
              <h4 className="text-sm font-medium text-red-900 dark:text-red-100">
                ⚠️ Risque de pénalités estimé
              </h4>
              <p className="mt-1 text-xs text-red-800 dark:text-red-200">
                En cas de retard sur toutes les obligations: jusqu'à{' '}
                <span className="font-bold">
                  {analysis.estimated_penalty_risk.toLocaleString('fr-DZ')} DZD
                </span>{' '}
                de pénalités de base (sans compter les intérêts de retard).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
        <div className="flex gap-3">
          <FileText className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
              📅 Prochaines étapes
            </h4>
            <p className="mt-1 text-xs text-blue-800 dark:text-blue-200">
              Après confirmation, nous générerons automatiquement votre calendrier de conformité
              pour l'année en cours. Vous recevrez des notifications {' '}
              <strong>7 jours, 3 jours et 1 jour avant</strong> chaque échéance.
            </p>
            <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
              📊 Vous aurez <strong>{costEstimate.total_annual} déclarations par an</strong>:{' '}
              {costEstimate.monthly_obligations} mensuelles, {costEstimate.quarterly_obligations} trimestrielles,{' '}
              et {costEstimate.annual_obligations} annuelles.
            </p>
          </div>
        </div>
      </div>

      {/* Real Data Badge */}
      <div className="rounded-lg bg-green-50 p-4 dark:bg-green-950 border border-green-200 dark:border-green-800">
        <div className="flex gap-3">
          <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <h4 className="text-sm font-medium text-green-900 dark:text-green-100">
              ✅ Données officielles 2024-2025
            </h4>
            <p className="mt-1 text-xs text-green-800 dark:text-green-200">
              Ces obligations sont basées sur les Lois de Finances 2024 et 2025, les circulaires de la DGI,
              et les règlements de la CNAS. Sources: Finance Law 2024 (TAP supprimée), Finance Law 2025
              (nouveaux délais), CNAS 2024-2025, Code des Impôts Directs et Taxes Assimilées (CIDTA).
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
        <Button type="button" onClick={onConfirm}>
          <Save className="mr-2 h-4 w-4" />
          Confirmer et terminer
        </Button>
      </div>
    </div>
  );
}
